import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles, useTheme, fade } from "@material-ui/core/styles";
import {
  Button, CircularProgress, Collapse, Dialog, Drawer, Fab, IconButton, InputAdornment, MenuItem,
  Paper, Popover, TextField, Tooltip, Typography, useMediaQuery
} from "@material-ui/core";
import AllInclusiveRoundedIcon from "@material-ui/icons/AllInclusiveRounded";
import SettingsRoundedIcon from "@material-ui/icons/SettingsRounded";
import RefreshRoundedIcon from "@material-ui/icons/RefreshRounded";
import KeyboardArrowDownRoundedIcon from "@material-ui/icons/KeyboardArrowDownRounded";
import SendRoundedIcon from "@material-ui/icons/SendRounded";
import AutorenewRoundedIcon from "@material-ui/icons/AutorenewRounded";
import CloseRoundedIcon from "@material-ui/icons/CloseRounded";
import SearchRoundedIcon from "@material-ui/icons/SearchRounded";
import EventRoundedIcon from "@material-ui/icons/EventRounded";
import ChevronLeftRoundedIcon from "@material-ui/icons/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@material-ui/icons/ChevronRightRounded";
import AutoAwesomeIcon from "@material-ui/icons/EmojiObjectsRounded";
import PaymentsRoundedIcon from "@material-ui/icons/AccountBalanceWalletRounded";
import FlagRoundedIcon from "@material-ui/icons/FlagRounded";
import PriceCheckRoundedIcon from "@material-ui/icons/LocalOfferRounded";
import ChatRoundedIcon from "@material-ui/icons/ChatRounded";
import VisibilityRoundedIcon from "@material-ui/icons/VisibilityOffRounded";
import VisibilityOnIcon from "@material-ui/icons/VisibilityRounded";
import FileCopyRoundedIcon from "@material-ui/icons/FileCopyRounded";
import DeleteSweepRoundedIcon from "@material-ui/icons/DeleteSweepRounded";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid,
  PieChart, Pie, Cell, BarChart, Bar
} from "recharts";
import { toast } from "react-toastify";
import { addDays, format, startOfMonth, endOfMonth, subMonths, subDays } from "date-fns";

import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const AZUL = "#0866FF";
const ROXO = "#7B2FF7";
const CORES = ["#0866FF", "#7B2FF7", "#14b8a6", "#f59e0b", "#ef4444", "#64748b"];

const useStyles = makeStyles(theme => {
  const escuro = theme.palette.type === "dark";
  const cartao = {
    borderRadius: 18,
    border: `1px solid ${escuro ? "rgba(255,255,255,.08)" : "rgba(15,23,42,.07)"}`,
    background: escuro ? "rgba(255,255,255,.03)" : "#fff",
    boxShadow: escuro ? "none" : "0 1px 2px rgba(15,23,42,.04), 0 8px 24px rgba(15,23,42,.04)",
  };
  return {
    pagina: { padding: "8px 8px 96px", display: "flex", flexDirection: "column", gap: 20 },
    topo: { display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" },
    logo: {
      width: 52, height: 52, borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center",
      background: `linear-gradient(135deg,${AZUL},${ROXO})`, color: "#fff", boxShadow: `0 8px 20px ${fade(AZUL, .3)}`,
      "& svg": { fontSize: 30 },
    },
    contaChip: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
    contaPonto: { width: 10, height: 10, borderRadius: 999, background: "#16a34a", boxShadow: "0 0 0 4px rgba(22,163,74,.15)", flexShrink: 0 },
    titulo: { fontWeight: 800, fontSize: 24, lineHeight: 1.1 },
    sub: { opacity: .65, fontSize: 13.5, marginTop: 4 },
    acoes: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginLeft: "auto" },
    selo: {
      display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 999, fontSize: 13, fontWeight: 600,
    },
    seletor: {
      minWidth: 190,
      "& .MuiOutlinedInput-root": { borderRadius: 12, height: 44, background: escuro ? "transparent" : "#fff" },
    },
    botao: { borderRadius: 12, height: 44, padding: "0 18px", textTransform: "none", fontWeight: 600 },
    iconeBotao: { borderRadius: 12, width: 44, height: 44, border: `1px solid ${theme.palette.divider}` },

    herois: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 16 },
    heroi: { ...cartao, padding: 20, display: "flex", gap: 16, alignItems: "center" },
    heroiIcone: { width: 48, height: 48, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
    heroiRotulo: { fontSize: 13, opacity: .65, fontWeight: 500 },
    heroiValor: { fontSize: 26, fontWeight: 800, lineHeight: 1.15, marginTop: 2 },

    faixa: { ...cartao, padding: "6px 8px", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))" },
    mini: { padding: "12px 14px", borderRight: `1px solid ${theme.palette.divider}`, "&:last-child": { borderRight: 0 } },
    miniRotulo: { fontSize: 12, opacity: .6 },
    miniValor: { fontSize: 18, fontWeight: 700, marginTop: 2 },

    graficos: { display: "grid", gridTemplateColumns: "minmax(0,2fr) minmax(0,1fr)", gap: 16,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "minmax(0,1fr)" } },
    cartao: { ...cartao, padding: 20 },
    cartaoTitulo: { fontWeight: 700, fontSize: 15 },
    cartaoSub: { fontSize: 12.5, opacity: .6, marginTop: 2 },
    legenda: { display: "flex", gap: 16, fontSize: 12.5, opacity: .8, "& span": { display: "inline-flex", alignItems: "center", gap: 6 } },
    ponto: { width: 10, height: 10, borderRadius: 3, display: "inline-block" },

    camps: { ...cartao, overflow: "hidden" },
    campsTopo: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "18px 20px" },
    busca: { minWidth: 240, "& .MuiOutlinedInput-root": { borderRadius: 12, height: 42 } },
    linha: {
      display: "grid", gridTemplateColumns: "minmax(220px,2.4fr) 110px repeat(6,minmax(80px,1fr)) 40px", alignItems: "center",
      gap: 8, padding: "14px 20px", borderTop: `1px solid ${theme.palette.divider}`, fontSize: 13.5, cursor: "pointer",
      transition: "background .15s", "&:hover": { background: escuro ? "rgba(255,255,255,.03)" : "#f8fafc" },
    },
    cab: { cursor: "default", fontSize: 12, fontWeight: 600, opacity: .6, textTransform: "uppercase", letterSpacing: .4,
      "&:hover": { background: "transparent" } },
    num: { textAlign: "right", fontVariantNumeric: "tabular-nums" },
    rolagem: { overflowX: "auto" },
    larg: { minWidth: 980 },
    status: { display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600 },
    barra: { height: 4, borderRadius: 4, background: fade(AZUL, .12), marginTop: 6, overflow: "hidden", "& > div": { height: "100%", background: AZUL } },
    ads: { padding: "4px 20px 18px 40px", display: "grid", gap: 10, background: escuro ? "rgba(255,255,255,.02)" : "#fafbfc" },
    ad: { display: "grid", gridTemplateColumns: "52px minmax(180px,2fr) repeat(5,minmax(80px,1fr))", gap: 12, alignItems: "center",
      padding: 10, borderRadius: 12, background: escuro ? "rgba(255,255,255,.03)" : "#fff", border: `1px solid ${theme.palette.divider}`, fontSize: 13 },
    thumb: { width: 52, height: 52, borderRadius: 10, objectFit: "cover", background: fade(AZUL, .1) },

    aviso: { ...cartao, padding: 48, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 },
    erro: { ...cartao, padding: 16, borderColor: "#fca5a5", background: escuro ? "rgba(239,68,68,.08)" : "#fef2f2", color: "#b91c1c" },

    fab: {
      position: "fixed", right: 28, bottom: 28, zIndex: 1200, textTransform: "none", fontWeight: 700, padding: "0 22px 0 16px",
      background: `linear-gradient(135deg,${AZUL},${ROXO})`, color: "#fff", boxShadow: `0 10px 28px ${fade(ROXO, .4)}`,
      "&:hover": { filter: "brightness(1.08)" }, "& svg": { marginRight: 8 },
    },
    gaveta: { width: 460, maxWidth: "100vw", height: "100%", display: "flex", flexDirection: "column" },
    gavetaTopo: { padding: "18px 20px", display: "flex", alignItems: "center", gap: 12, color: "#fff",
      background: `linear-gradient(135deg,${AZUL},${ROXO})` },
    msgs: { flex: 1, overflowY: "auto", padding: 20, display: "flex", flexDirection: "column", gap: 12 },
    balao: { padding: "12px 16px", borderRadius: 16, fontSize: 14, lineHeight: 1.55, maxWidth: "90%", wordBreak: "break-word" },
    eu: { alignSelf: "flex-end", background: `linear-gradient(135deg,${AZUL},${ROXO})`, color: "#fff", borderBottomRightRadius: 4 },
    ia: { alignSelf: "flex-start", background: escuro ? "rgba(255,255,255,.07)" : "#f1f5f9", borderBottomLeftRadius: 4 },
    sugestao: {
      textAlign: "left", padding: "12px 14px", borderRadius: 12, border: `1px solid ${theme.palette.divider}`, cursor: "pointer",
      background: "transparent", color: "inherit", font: "inherit", fontSize: 13.5, transition: "all .15s",
      "&:hover": { borderColor: ROXO, background: fade(ROXO, .05) },
    },
    entrada: { padding: 16, borderTop: `1px solid ${theme.palette.divider}`, display: "flex", gap: 10, alignItems: "flex-end",
      "& .MuiOutlinedInput-root": { borderRadius: 14 } },
    enviar: { width: 48, height: 48, borderRadius: 14, color: "#fff", background: `linear-gradient(135deg,${AZUL},${ROXO})`,
      "&.Mui-disabled": { opacity: .4, color: "#fff" } },

    modal: { borderRadius: 20, width: 540, maxWidth: "calc(100vw - 32px)" },
    modalTopo: { padding: "24px 28px 8px", display: "flex", alignItems: "center", gap: 14 },
    modalCorpo: { padding: "12px 28px 4px", display: "flex", flexDirection: "column", gap: 22 },
    dica: { fontSize: 13, lineHeight: 1.6, padding: 14, borderRadius: 12, background: fade(AZUL, .06), border: `1px solid ${fade(AZUL, .15)}` },
    campo: { "& .MuiOutlinedInput-root": { borderRadius: 12, minHeight: 50 }, "& .MuiOutlinedInput-input": { padding: "14px 16px" },
      "& .MuiSelect-select": { padding: "14px 40px 14px 16px" } },
    calPapel: { borderRadius: 18, marginTop: 8, boxShadow: "0 20px 50px rgba(15,23,42,.18)", maxWidth: "calc(100vw - 24px)" },
    calCorpo: { display: "flex", [theme.breakpoints.down("xs")]: { flexDirection: "column" } },
    calPresets: { display: "flex", flexDirection: "column", gap: 2, padding: 10, minWidth: 170, borderRight: `1px solid ${theme.palette.divider}`,
      [theme.breakpoints.down("xs")]: { flexDirection: "row", flexWrap: "wrap", borderRight: 0, borderBottom: `1px solid ${theme.palette.divider}` } },
    calPreset: { textAlign: "left", background: "none", border: 0, color: "inherit", font: "inherit", fontSize: 13.5, padding: "10px 14px",
      borderRadius: 10, cursor: "pointer", "&:hover": { background: fade(theme.palette.primary.main, .07) } },
    calPresetAtivo: { background: fade(theme.palette.primary.main, .12), color: theme.palette.primary.main, fontWeight: 700 },
    calMes: { display: "grid", gridTemplateColumns: "repeat(7,38px)", rowGap: 4 },
    calSemana: { textAlign: "center", fontSize: 11.5, fontWeight: 700, opacity: .45, padding: "4px 0" },
    calDia: { width: 38, height: 36, border: 0, background: "none", color: "inherit", font: "inherit", fontSize: 13, cursor: "pointer",
      borderRadius: 10, "&:hover:not(:disabled)": { background: fade(theme.palette.primary.main, .12) }, "&:disabled": { opacity: .25, cursor: "default" } },
    calDentro: { background: fade(theme.palette.primary.main, .1), borderRadius: 0 },
    calPonta: { background: `${theme.palette.primary.main} !important`, color: "#fff", fontWeight: 700 },
    calHoje: { boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}` },
    calRodape: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginTop: 14,
      paddingTop: 14, borderTop: `1px solid ${theme.palette.divider}` },
    drawer: { width: 480, maxWidth: "100vw", minHeight: "100%", display: "flex", flexDirection: "column" },
    drTopo: { position: "sticky", top: 0, zIndex: 2, display: "flex", gap: 12, alignItems: "flex-start", padding: "22px 22px 18px",
      background: theme.palette.background.paper, borderBottom: `1px solid ${theme.palette.divider}` },
    drCorpo: { padding: 22, display: "flex", flexDirection: "column", gap: 22 },
    drSecao: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: .8, opacity: .55, marginBottom: 10 },
    drGrade: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
    drItem: { padding: 14, borderRadius: 14, border: `1px solid ${theme.palette.divider}`, background: escuro ? "rgba(255,255,255,.03)" : "#f8fafc" },
    drItemRot: { fontSize: 11.5, opacity: .6, fontWeight: 600 },
    drItemVal: { fontSize: 20, fontWeight: 800, marginTop: 4 },
    drCaixa: { padding: 16, borderRadius: 14, border: `1px solid ${theme.palette.divider}`, background: escuro ? "rgba(255,255,255,.03)" : "#fff" },
    drLinha: { display: "flex", justifyContent: "space-between", gap: 12, padding: "10px 0", fontSize: 13, borderBottom: `1px solid ${theme.palette.divider}`,
      "&:last-child": { borderBottom: 0 }, "& span": { opacity: .65 }, "& b": { textAlign: "right", wordBreak: "break-all" } },
    insight: { padding: 16, borderRadius: 14, fontSize: 13.5, lineHeight: 1.55, background: `linear-gradient(135deg,${fade(AZUL, .07)},${fade(ROXO, .07)})`,
      border: `1px solid ${fade(ROXO, .18)}` },
    rotuloCampo: { fontSize: 13, fontWeight: 600, marginBottom: 8, opacity: .8 },
    tokenCaixa: { display: "flex", flexDirection: "column", gap: 8, padding: 14, borderRadius: 14,
      border: `1px solid ${theme.palette.divider}`, background: escuro ? "rgba(255,255,255,.03)" : "#f8fafc" },
    tokenOk: { width: 30, height: 30, borderRadius: 10, background: "#dcfce7", color: "#16a34a", display: "flex",
      alignItems: "center", justifyContent: "center", fontWeight: 800, flexShrink: 0 },
    tokenTexto: { fontFamily: "monospace", fontSize: 12, padding: "8px 10px", borderRadius: 8, overflow: "hidden", textOverflow: "ellipsis",
      whiteSpace: "nowrap", background: escuro ? "rgba(0,0,0,.25)" : "#fff", border: `1px solid ${theme.palette.divider}`, opacity: .85 },
    avancadoBtn: { display: "flex", alignItems: "center", gap: 6, background: "none", border: 0, padding: 0, cursor: "pointer",
      color: "inherit", font: "inherit", fontSize: 13.5, fontWeight: 600, opacity: .75, "&:hover": { opacity: 1 } },
    filtros: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "0 20px 16px" },
    chipF: { border: `1px solid ${theme.palette.divider}`, background: "transparent", color: "inherit", font: "inherit", fontSize: 13,
      fontWeight: 600, padding: "8px 14px", borderRadius: 999, cursor: "pointer", display: "inline-flex", gap: 6, alignItems: "center",
      transition: "all .15s", "& small": { opacity: .6, fontWeight: 500 } },
    chipAtivo: { borderColor: theme.palette.primary.main, background: fade(theme.palette.primary.main, .1), color: theme.palette.primary.main },
    filtroSel: { minWidth: 150, "& .MuiOutlinedInput-root": { borderRadius: 12, height: 40, fontSize: 13.5 } },
    ordenavel: { cursor: "pointer", userSelect: "none", "&:hover": { opacity: 1 } },
    modalRodape: { padding: "16px 28px 24px", display: "flex", justifyContent: "flex-end", gap: 10 },
  };
});

const PERIODOS = [
  ["today", "Hoje"], ["yesterday", "Ontem"], ["last_7d", "Últimos 7 dias"], ["last_14d", "Últimos 14 dias"],
  ["last_30d", "Últimos 30 dias"], ["this_month", "Este mês"], ["last_month", "Mês passado"], ["maximum", "Todo o período"],
];

const ATALHOS = [
  "Faça uma análise completa da conta neste período",
  "Qual campanha está com o melhor e o pior custo por resultado?",
  "Onde estou desperdiçando verba?",
  "Tem sinal de fadiga de criativo?",
  "Como redistribuir o orçamento?",
];

const brl = v => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const int = v => Number(v || 0).toLocaleString("pt-BR");
const pct = v => `${Number(v || 0).toFixed(2).replace(".", ",")}%`;

const STATUS = {
  ACTIVE: ["Ativa", "#16a34a"], PAUSED: ["Pausada", "#d97706"], ARCHIVED: ["Arquivada", "#64748b"], DELETED: ["Excluída", "#64748b"],
  CAMPAIGN_PAUSED: ["Campanha pausada", "#d97706"], ADSET_PAUSED: ["Conjunto pausado", "#d97706"], WITH_ISSUES: ["Com problema", "#dc2626"],
  IN_PROCESS: ["Em análise", "#2563eb"], PENDING_REVIEW: ["Em revisão", "#2563eb"], DISAPPROVED: ["Reprovado", "#dc2626"],
};
const Status = ({ s }) => {
  const classes = useStyles();
  const [nome, cor] = STATUS[s] || [s, "#64748b"];
  return <span className={classes.status} style={{ color: cor, background: fade(cor, .1) }}>● {nome}</span>;
};

// ── período ──
const paramsDe = p => (p?.since && p?.until ? { since: p.since, until: p.until } : { preset: p?.preset || "last_7d" });
const chaveDe = p => (p?.since ? `${p.since}_${p.until}` : p?.preset || "last_7d");
const dataBR = iso => { const [y, m, d] = String(iso).split("-"); return `${d}/${m}/${y}`; };
const rotuloPeriodo = p => (p?.since ? (p.since === p.until ? dataBR(p.since) : `${dataBR(p.since)} – ${dataBR(p.until)}`)
  : (PERIODOS.find(x => x[0] === p?.preset) || [null, "Últimos 7 dias"])[1]);

// Dias do período (para o gráfico não pular os dias sem gasto)
const diasDoPeriodo = p => {
  const hoje = new Date();
  const ontem = subDays(hoje, 1);
  const map = {
    today: [hoje, hoje], yesterday: [ontem, ontem], last_7d: [subDays(hoje, 7), ontem], last_14d: [subDays(hoje, 14), ontem],
    last_30d: [subDays(hoje, 30), ontem], this_month: [startOfMonth(hoje), hoje],
    last_month: [startOfMonth(subMonths(hoje, 1)), endOfMonth(subMonths(hoje, 1))],
  };
  const r = p?.since ? [new Date(`${p.since}T12:00:00`), new Date(`${p.until}T12:00:00`)] : map[p?.preset];
  if (!r) return null;
  const dias = [];
  for (let d = r[0]; d <= r[1] && dias.length < 400; d = addDays(d, 1)) dias.push(format(d, "yyyy-MM-dd"));
  return dias;
};

const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const SEMANA = ["D", "S", "T", "Q", "Q", "S", "S"];

const Mes = ({ ano, mes, ini, fim, passando, onDia, onPassar }) => {
  const classes = useStyles();
  const hojeIso = format(new Date(), "yyyy-MM-dd");
  const primeiro = new Date(ano, mes, 1);
  const total = new Date(ano, mes + 1, 0).getDate();
  const celulas = [];
  for (let i = 0; i < primeiro.getDay(); i++) celulas.push(null);
  for (let d = 1; d <= total; d++) celulas.push(format(new Date(ano, mes, d), "yyyy-MM-dd"));
  const fimVis = fim || (ini && passando && passando > ini ? passando : null);
  return (
    <div className={classes.calMes}>
      {SEMANA.map((s, i) => <div key={i} className={classes.calSemana}>{s}</div>)}
      {celulas.map((iso, i) => {
        if (!iso) return <div key={i} />;
        const futuro = iso > hojeIso;
        const ponta = iso === ini || iso === fimVis;
        const dentro = ini && fimVis && iso > ini && iso < fimVis;
        return (
          <button key={i} type="button" disabled={futuro} onClick={() => onDia(iso)} onMouseEnter={() => onPassar(iso)}
            className={`${classes.calDia} ${ponta ? classes.calPonta : ""} ${dentro ? classes.calDentro : ""} ${iso === hojeIso ? classes.calHoje : ""}`}>
            {Number(iso.slice(8))}
          </button>
        );
      })}
    </div>
  );
};

const SeletorPeriodo = ({ valor, onChange }) => {
  const classes = useStyles();
  const theme = useTheme();
  const duplo = useMediaQuery(theme.breakpoints.up("sm"));
  const [ancora, setAncora] = useState(null);
  const [ini, setIni] = useState(null);
  const [fim, setFim] = useState(null);
  const [passando, setPassando] = useState(null);
  const base = new Date();
  const [vista, setVista] = useState({ ano: base.getFullYear(), mes: base.getMonth() - 1 });

  const abrir = e => {
    setAncora(e.currentTarget);
    setIni(valor?.since || null); setFim(valor?.until || null); setPassando(null);
    const ref = valor?.until ? new Date(`${valor.until}T12:00:00`) : new Date();
    setVista({ ano: ref.getFullYear(), mes: ref.getMonth() - (duplo ? 1 : 0) });
  };
  const meses = (duplo ? [0, 1] : [0]).map(k => { const d = new Date(vista.ano, vista.mes + k, 1); return { ano: d.getFullYear(), mes: d.getMonth() }; });
  const andar = n => setVista(v => { const d = new Date(v.ano, v.mes + n, 1); return { ano: d.getFullYear(), mes: d.getMonth() }; });
  const clicarDia = iso => {
    if (!ini || fim) { setIni(iso); setFim(null); return; }
    if (iso < ini) { setFim(ini); setIni(iso); } else setFim(iso);
  };
  const aplicar = () => { if (!ini) return; onChange({ since: ini, until: fim || ini }); setAncora(null); };

  return (
    <>
      <Button variant="outlined" className={classes.botao} onClick={abrir} startIcon={<EventRoundedIcon />} endIcon={<KeyboardArrowDownRoundedIcon />}
        style={{ background: theme.palette.type === "dark" ? "transparent" : "#fff" }}>
        {rotuloPeriodo(valor)}
      </Button>
      <Popover open={!!ancora} anchorEl={ancora} onClose={() => setAncora(null)} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }} PaperProps={{ className: classes.calPapel }}>
        <div className={classes.calCorpo}>
          <div className={classes.calPresets}>
            {PERIODOS.map(([v, r]) => (
              <button key={v} type="button" className={`${classes.calPreset} ${!valor?.since && valor?.preset === v ? classes.calPresetAtivo : ""}`}
                onClick={() => { onChange({ preset: v }); setAncora(null); }}>{r}</button>
            ))}
          </div>
          <div style={{ padding: "16px 18px", flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
              <IconButton size="small" onClick={() => andar(-1)}><ChevronLeftRoundedIcon /></IconButton>
              <div style={{ flex: 1, display: "grid", gridTemplateColumns: `repeat(${meses.length},1fr)`, textAlign: "center", fontWeight: 700, fontSize: 14 }}>
                {meses.map(m => <div key={`${m.ano}-${m.mes}`}>{MESES[m.mes]} {m.ano}</div>)}
              </div>
              <IconButton size="small" onClick={() => andar(1)}><ChevronRightRoundedIcon /></IconButton>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${meses.length},1fr)`, gap: 24 }} onMouseLeave={() => setPassando(null)}>
              {meses.map(m => <Mes key={`${m.ano}-${m.mes}`} {...m} ini={ini} fim={fim} passando={passando} onDia={clicarDia} onPassar={setPassando} />)}
            </div>
            <div className={classes.calRodape}>
              <div style={{ fontSize: 13, opacity: .75 }}>
                {ini ? <>De <b>{dataBR(ini)}</b> até <b>{dataBR(fim || ini)}</b></> : "Clique no dia inicial e depois no final"}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Button onClick={() => setAncora(null)} style={{ textTransform: "none", borderRadius: 10 }}>Cancelar</Button>
                <Button variant="contained" color="primary" disableElevation disabled={!ini} onClick={aplicar} style={{ textTransform: "none", borderRadius: 10, fontWeight: 600 }}>Aplicar</Button>
              </div>
            </div>
          </div>
        </div>
      </Popover>
    </>
  );
};

// Markdown simples da I.A: títulos, listas e **negrito**
const negrito = t => String(t).split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
  p.startsWith("**") && p.endsWith("**") ? <b key={i}>{p.slice(2, -2)}</b> : <React.Fragment key={i}>{p}</React.Fragment>);
const Formatado = ({ texto }) => (
  <>
    {String(texto || "").split("\n").map((l, i) => {
      const s = l.trim();
      if (!s) return <div key={i} style={{ height: 6 }} />;
      if (/^#{1,4}\s/.test(s)) return <div key={i} style={{ fontWeight: 700, fontSize: 15, margin: "6px 0 2px" }}>{negrito(s.replace(/^#+\s/, ""))}</div>;
      if (/^[-*•]\s/.test(s)) return <div key={i} style={{ display: "flex", gap: 8, paddingLeft: 4 }}><span>•</span><span>{negrito(s.slice(2))}</span></div>;
      if (/^\d+[.)]\s/.test(s)) { const m = s.match(/^(\d+[.)])\s(.*)$/); return <div key={i} style={{ display: "flex", gap: 8 }}><b>{m[1]}</b><span>{negrito(m[2])}</span></div>; }
      return <div key={i}>{negrito(s)}</div>;
    })}
  </>
);

const Heroi = ({ icone, cor, rotulo, valor }) => {
  const classes = useStyles();
  return (
    <div className={classes.heroi}>
      <div className={classes.heroiIcone} style={{ background: fade(cor, .12), color: cor }}>{icone}</div>
      <div style={{ minWidth: 0 }}>
        <div className={classes.heroiRotulo}>{rotulo}</div>
        <div className={classes.heroiValor}>{valor}</div>
      </div>
    </div>
  );
};

const DicaGrafico = ({ active, payload, label }) => {
  const theme = useTheme();
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: theme.palette.background.paper, borderRadius: 12, padding: "10px 14px", fontSize: 13,
      boxShadow: "0 8px 24px rgba(0,0,0,.12)", border: `1px solid ${theme.palette.divider}` }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ color: p.color }}>{p.name}: <b>{p.dataKey === "gasto" ? brl(p.value) : int(p.value)}</b></div>
      ))}
    </div>
  );
};

const LinhaCampanha = ({ c, maior, onAbrir }) => {
  const classes = useStyles();
  return (
    <div className={classes.linha} onClick={() => onAbrir(c)}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={c.nome}>{c.nome}</div>
        <div style={{ fontSize: 12, opacity: .6, marginTop: 2 }}>
          {[c.objetivo && (NOME_OBJ[objetivoDe(c)] || objetivoDe(c)), c.orcamentoDiario && `${brl(c.orcamentoDiario)}/dia`].filter(Boolean).join(" · ")}
        </div>
        {maior > 0 && c.gasto > 0 && <div className={classes.barra}><div style={{ width: `${Math.max(2, (c.gasto / maior) * 100)}%` }} /></div>}
      </div>
      <div><Status s={c.status} /></div>
      <div className={classes.num} style={{ fontWeight: 700 }}>{brl(c.gasto)}</div>
      <div className={classes.num}>{int(c.impressoes)}</div>
      <div className={classes.num}>{int(c.cliques)}</div>
      <div className={classes.num}>{pct(c.ctr)}</div>
      <div className={classes.num}>{int(c.resultados)}</div>
      <div className={classes.num}>{c.resultados ? brl(c.custoPorResultado) : "—"}</div>
      <div style={{ textAlign: "right" }}><ChevronRightRoundedIcon style={{ opacity: .5 }} /></div>
    </div>
  );
};

const NOME_OBJ = { traffic: "Tráfego", leads: "Leads", engagement: "Engajamento", sales: "Vendas", awareness: "Reconhecimento",
  app_promotion: "Promoção de app", messages: "Mensagens", link_clicks: "Cliques no link", conversions: "Conversões" };
const objetivoDe = c => (c?.objetivo || "").replace("OUTCOME_", "").toLowerCase();

// Diagnóstico rápido (sem I.A) para a barra lateral
const insightDe = (c, t) => {
  const ativa = c.status === "ACTIVE";
  if (!ativa) return t.gasto > 0 ? `Campanha parada. No período gastou **${brl(t.gasto)}** e trouxe **${int(t.resultados)}** resultado(s).` : "Campanha parada e sem gasto no período.";
  if (!t.gasto) return "Campanha ativa mas **sem gasto** no período. Confira se os conjuntos/anúncios estão ativos, aprovados e com orçamento.";
  if (!t.resultados && t.gasto > 20) return `Gastou **${brl(t.gasto)}** sem resultados. Verifique o objetivo, o público e se o link do WhatsApp/formulário está funcionando.`;
  if (t.frequencia > 4) return `Frequência de **${t.frequencia.toFixed(1)}x**: o público está vendo o anúncio demais. Troque o criativo ou amplie o público.`;
  if (t.impressoes > 1000 && t.ctr < 1) return `CTR de **${pct(t.ctr)}** está baixo. Teste outro criativo com uma chamada mais forte logo no início.`;
  if (t.resultados && t.custoPorResultado <= 5) return `Custo por resultado de **${brl(t.custoPorResultado)}** está muito bom. Se a qualidade dos candidatos estiver boa, vale aumentar o orçamento aos poucos (20% a cada 2–3 dias).`;
  return `Campanha rodando: **${int(t.resultados)}** resultado(s) a **${brl(t.custoPorResultado)}** cada. Acompanhe a frequência (${t.frequencia.toFixed(1)}x) e o custo diariamente.`;
};

const CampanhaDrawer = ({ campanha, periodo, onClose, onPerguntar }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState("");

  useEffect(() => {
    if (!campanha) return;
    setDados(null); setErro("");
    api.get(`/meta/campanhas/${campanha.id}`, { params: paramsDe(periodo) })
      .then(r => setDados(r.data))
      .catch(e => setErro(e?.response?.data?.error || "Não foi possível carregar a campanha."));
  }, [campanha, periodo]);

  const c = dados?.campanha || campanha || {};
  const t = dados?.totais || campanha || {};
  const serie = useMemo(() => {
    const mapa = new Map((dados?.porDia || []).map(d => [d.dia, d]));
    const dias = diasDoPeriodo(periodo) || (dados?.porDia || []).map(d => d.dia);
    return dias.map(d => ({ dia: `${d.slice(8, 10)}/${d.slice(5, 7)}`, gasto: mapa.get(d)?.gasto || 0, resultados: mapa.get(d)?.resultados || 0 }));
  }, [dados, periodo]);
  const data = v => (v ? new Date(v).toLocaleDateString("pt-BR") : "—");
  const corCtr = t.ctr >= 2 ? "#16a34a" : t.ctr >= 1 ? AZUL : "#d97706";
  const corFreq = t.frequencia <= 3 ? "#16a34a" : t.frequencia <= 5 ? "#d97706" : "#dc2626";
  const eixo = { fontSize: 11, fill: theme.palette.text.secondary };

  const Linha = ({ r, v }) => <div className={classes.drLinha}><span>{r}</span><b>{v}</b></div>;

  return (
    <Drawer anchor="right" open={!!campanha} onClose={onClose}>
      <div className={classes.drawer}>
        <div className={classes.drTopo}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, opacity: .6, marginBottom: 4 }}>Campanha · {rotuloPeriodo(periodo)}</div>
            <div style={{ fontWeight: 800, fontSize: 17, lineHeight: 1.3 }}>{c.nome}</div>
            <div style={{ marginTop: 8 }}><Status s={c.status} /></div>
          </div>
          <IconButton onClick={onClose} className={classes.iconeBotao} style={{ width: 38, height: 38 }}><CloseRoundedIcon /></IconButton>
        </div>

        <div className={classes.drCorpo}>
          {erro && <div className={classes.erro}>{erro}</div>}

          <div>
            <div className={classes.drSecao}>Desempenho</div>
            <div className={classes.drGrade}>
              {[["Investimento", brl(t.gasto), null], ["Resultados", int(t.resultados), "#16a34a"],
                ["Custo/resultado", t.resultados ? brl(t.custoPorResultado) : "—", ROXO], ["CTR", pct(t.ctr), corCtr]].map(([r, v, cor]) => (
                <div key={r} className={classes.drItem}><div className={classes.drItemRot}>{r}</div><div className={classes.drItemVal} style={cor ? { color: cor } : null}>{v}</div></div>
              ))}
            </div>
          </div>

          <div className={classes.insight}>
            <div style={{ fontWeight: 700, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}><AutoAwesomeIcon fontSize="small" /> Diagnóstico rápido</div>
            <Formatado texto={insightDe(c, t)} />
            <Button size="small" onClick={() => onPerguntar(`Analise a fundo a campanha "${c.nome}" e me diga o que melhorar.`)}
              style={{ marginTop: 8, textTransform: "none", fontWeight: 600, borderRadius: 10, color: ROXO }}>Perguntar à Analista I.A →</Button>
          </div>

          {!dados && !erro ? <div style={{ textAlign: "center", padding: 24 }}><CircularProgress size={26} /></div> : dados && (
            <>
              {serie.length > 1 && (
                <div>
                  <div className={classes.drSecao}>Gasto por dia</div>
                  <div className={classes.drCaixa} style={{ padding: "12px 6px 4px" }}>
                    <ResponsiveContainer width="100%" height={150}>
                      <AreaChart data={serie} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                        <defs><linearGradient id="gDr" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={AZUL} stopOpacity={0.3} /><stop offset="100%" stopColor={AZUL} stopOpacity={0} /></linearGradient></defs>
                        <XAxis dataKey="dia" tick={eixo} axisLine={false} tickLine={false} minTickGap={14} />
                        <YAxis tick={eixo} axisLine={false} tickLine={false} width={44} tickFormatter={v => `R$${v}`} />
                        <RTooltip content={<DicaGrafico />} />
                        <Area type="monotone" dataKey="gasto" name="Gasto" stroke={AZUL} strokeWidth={2} fill="url(#gDr)" />
                        <Area type="monotone" dataKey="resultados" name="Resultados" stroke={ROXO} strokeWidth={2} fill="none" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              <div>
                <div className={classes.drSecao}>Indicadores</div>
                <div className={classes.drCaixa}>
                  {[["CTR", pct(t.ctr), Math.min((t.ctr || 0) / 3, 1), corCtr, "bom acima de 1%"],
                    ["Frequência", `${Number(t.frequencia || 0).toFixed(1)}x`, Math.min((t.frequencia || 0) / 6, 1), corFreq, "ideal até 3x"]].map(([r, v, p, cor, dica]) => (
                    <div key={r} style={{ marginBottom: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, marginBottom: 6 }}>
                        <span style={{ opacity: .7 }}>{r} <small style={{ opacity: .7 }}>· {dica}</small></span><b>{v}</b>
                      </div>
                      <div style={{ height: 8, borderRadius: 8, background: theme.palette.divider, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${p * 100}%`, background: cor, borderRadius: 8, transition: "width .6s" }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className={classes.drSecao}>Métricas detalhadas</div>
                <div className={classes.drCaixa} style={{ padding: "4px 16px" }}>
                  <Linha r="Impressões" v={int(t.impressoes)} />
                  <Linha r="Alcance" v={int(t.alcance)} />
                  <Linha r="Cliques" v={int(t.cliques)} />
                  <Linha r="Cliques no link" v={int(t.cliquesNoLink)} />
                  <Linha r="Conversas iniciadas" v={int(t.conversas)} />
                  <Linha r="Leads" v={int(t.leads)} />
                  <Linha r="CPC" v={brl(t.cpc)} />
                  <Linha r="CPM" v={brl(t.cpm)} />
                </div>
              </div>

              <div>
                <div className={classes.drSecao}>Informações</div>
                <div className={classes.drCaixa} style={{ padding: "4px 16px" }}>
                  <Linha r="Objetivo" v={NOME_OBJ[objetivoDe(c)] || objetivoDe(c) || "—"} />
                  <Linha r="Orçamento" v={c.orcamentoDiario ? `${brl(c.orcamentoDiario)}/dia` : c.orcamentoTotal ? `${brl(c.orcamentoTotal)} total` : "No conjunto"} />
                  {c.orcamentoRestante != null && <Linha r="Orçamento restante" v={brl(c.orcamentoRestante)} />}
                  <Linha r="Início" v={data(c.inicio)} />
                  {c.fim && <Linha r="Término" v={data(c.fim)} />}
                  <Linha r="Criada em" v={data(c.criadoEm)} />
                  <Linha r="ID" v={c.id} />
                </div>
              </div>

              <div>
                <div className={classes.drSecao}>Anúncios ({dados.anuncios.length})</div>
                <div style={{ display: "grid", gap: 10 }}>
                  {!dados.anuncios.length && <div style={{ opacity: .6, fontSize: 13 }}>Nenhum anúncio.</div>}
                  {dados.anuncios.map(a => (
                    <div key={a.id} className={classes.drCaixa} style={{ padding: 12 }}>
                      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                        {a.imagem ? <img src={a.imagem} alt="" className={classes.thumb} /> : <div className={classes.thumb} />}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.nome}>{a.nome}</div>
                          {a.conjunto && <div style={{ fontSize: 12, opacity: .6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.conjunto}</div>}
                          <div style={{ marginTop: 4 }}><Status s={a.status} /></div>
                        </div>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginTop: 12 }}>
                        {[["Gasto", brl(a.gasto)], ["Result.", int(a.resultados)], ["Custo", a.resultados ? brl(a.custoPorResultado) : "—"], ["CTR", pct(a.ctr)]].map(([r, v]) => (
                          <div key={r}><div style={{ fontSize: 11, opacity: .55 }}>{r}</div><div style={{ fontWeight: 700, fontSize: 13 }}>{v}</div></div>
                        ))}
                      </div>
                      {a.texto && <div style={{ fontSize: 12, opacity: .7, marginTop: 10, lineHeight: 1.5, maxHeight: 54, overflow: "hidden" }}>{a.texto}</div>}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
};

const ConfigModal = ({ open, onClose, status, onSalvo }) => {
  const classes = useStyles();
  const [token, setToken] = useState("");
  const [trocando, setTrocando] = useState(false);
  const [inteiro, setInteiro] = useState(false);
  const [conta, setConta] = useState("");
  const [appId, setAppId] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [avancado, setAvancado] = useState(false);
  const [contas, setContas] = useState([]);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setToken(""); setTrocando(!status?.token); setInteiro(false);
    const idApp = /^\d+$/.test(status?.appId || "") ? status.appId : "";
    setConta(status?.contaId || ""); setAppId(idApp); setAppSecret("");
    setAvancado(!!idApp);
    if (status?.configurado) api.get("/meta/contas").then(r => setContas(r.data || [])).catch(() => setContas([]));
  }, [open, status]);

  const salvar = async () => {
    setSalvando(true);
    try {
      const corpo = { accountId: conta, appId, appSecret };
      if (trocando && token.trim()) corpo.accessToken = token.trim();
      const { data } = await api.put("/meta/config", corpo);
      onSalvo(data);
      let lista = contas;
      try { const r = await api.get("/meta/contas"); lista = r.data || []; setContas(lista); } catch (e) { /* */ }
      if (data?.contaId) { toast.success("Configuração da Meta salva."); onClose(); }
      else { setTrocando(false); toast.info(lista.length ? "Token salvo! Agora escolha a conta de anúncios." : "Token salvo."); }
    } catch (e) { toastError(e); }
    setSalvando(false);
  };
  const copiar = () => { try { navigator.clipboard.writeText(status?.token || ""); toast.success("Token copiado."); } catch (e) { /* */ } };
  const expira = status?.expiraEm;
  const semAuto = { autoComplete: "off", "data-lpignore": "true", "data-1p-ignore": "true", "data-form-type": "other" };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ className: classes.modal }}>
      <div className={classes.modalTopo}>
        <div className={classes.logo} style={{ width: 44, height: 44, borderRadius: 14 }}><AllInclusiveRoundedIcon style={{ fontSize: 24 }} /></div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 19 }}>Conectar Meta</div>
          <div style={{ fontSize: 13, opacity: .6 }}>Anúncios do Facebook e Instagram</div>
        </div>
        <IconButton onClick={onClose}><CloseRoundedIcon /></IconButton>
      </div>

      <form className={classes.modalCorpo} autoComplete="off" onSubmit={e => { e.preventDefault(); salvar(); }}>
        <div>
          <div className={classes.rotuloCampo}>Token de acesso</div>
          {!trocando ? (
            <div className={classes.tokenCaixa}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span className={classes.tokenOk}>✓</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>Token conectado</div>
                  <div style={{ fontSize: 12.5, opacity: .65 }}>
                    {status?.usuario ? `${status.usuario} · ` : ""}{expira === "nunca" ? "não expira" : expira ? `expira em ${new Date(expira).toLocaleDateString("pt-BR")}` : ""}
                  </div>
                </div>
                <Tooltip title={inteiro ? "Recolher" : "Ver token inteiro"}>
                  <IconButton size="small" onClick={() => setInteiro(v => !v)}>{inteiro ? <VisibilityRoundedIcon fontSize="small" /> : <VisibilityOnIcon fontSize="small" />}</IconButton>
                </Tooltip>
                <Tooltip title="Copiar"><IconButton size="small" onClick={copiar}><FileCopyRoundedIcon fontSize="small" /></IconButton></Tooltip>
              </div>
              <div className={classes.tokenTexto} style={inteiro ? { whiteSpace: "normal", wordBreak: "break-all" } : null}>{status?.token}</div>
              <Button size="small" color="primary" onClick={() => setTrocando(true)} style={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 600, marginTop: 4 }}>
                Trocar token
              </Button>
            </div>
          ) : (
            <>
              <TextField className={classes.campo} fullWidth variant="outlined" placeholder="Cole aqui o token (começa com EAA...)" value={token}
                onChange={e => setToken(e.target.value)} inputProps={{ ...semAuto, name: "meta-tk", style: { fontFamily: "monospace", fontSize: 13 } }} />
              <div className={classes.dica} style={{ marginTop: 10 }}>
                💡 Gere em <b>Gerenciador de Negócios → Usuários do sistema → Gerar token</b>, validade <b>Nunca</b>, permissões <b>ads_read</b> e <b>read_insights</b>.
              </div>
              {status?.token && <Button size="small" onClick={() => setTrocando(false)} style={{ textTransform: "none", marginTop: 6 }}>Cancelar troca</Button>}
            </>
          )}
        </div>

        <div>
          <div className={classes.rotuloCampo}>Conta de anúncios</div>
          {contas.length ? (
            <TextField select className={classes.campo} fullWidth variant="outlined" value={conta} onChange={e => setConta(e.target.value)}
              SelectProps={{ displayEmpty: true, renderValue: v => { const c = contas.find(x => x.id === v); return c ? `${c.nome} · ${c.id}` : <span style={{ opacity: .5 }}>Escolha a conta</span>; },
                MenuProps: { getContentAnchorEl: null, anchorOrigin: { vertical: "bottom", horizontal: "left" }, PaperProps: { style: { borderRadius: 12, marginTop: 6 } } } }}>
              {contas.map(c => (
                <MenuItem key={c.id} value={c.id} style={{ padding: "12px 16px" }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{c.nome}{c.ativa ? "" : " (inativa)"}</div>
                    <div style={{ fontSize: 12, opacity: .6 }}>ID {c.id}</div>
                  </div>
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField className={classes.campo} fullWidth variant="outlined" placeholder="Salve o token primeiro para listar as contas" value={conta}
              onChange={e => setConta(e.target.value)} inputProps={{ ...semAuto, name: "meta-conta" }} />
          )}
        </div>

        <div>
          <button type="button" className={classes.avancadoBtn} onClick={() => setAvancado(v => !v)}>
            <KeyboardArrowDownRoundedIcon style={{ transition: "transform .2s", transform: avancado ? "rotate(180deg)" : "none" }} />
            Avançado: renovar token comum (60 dias)
          </button>
          <Collapse in={avancado}>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 12 }}>
              <div style={{ fontSize: 12.5, opacity: .65 }}>Só precisa se o token NÃO for de Usuário do sistema. Fica em developers.facebook.com → seu app → Configurações → Básico.</div>
              <TextField className={classes.campo} fullWidth variant="outlined" placeholder="App ID (só números)" value={appId}
                onChange={e => setAppId(e.target.value.replace(/\D/g, ""))} inputProps={{ ...semAuto, name: "meta-app", inputMode: "numeric" }} />
              <TextField className={classes.campo} fullWidth variant="outlined" value={appSecret} onChange={e => setAppSecret(e.target.value)}
                placeholder={status?.appConfigurado ? "App Secret salvo (preencha só para trocar)" : "App Secret"}
                inputProps={{ ...semAuto, name: "meta-sec", style: { WebkitTextSecurity: "disc" } }} />
            </div>
          </Collapse>
        </div>
      </form>

      <div className={classes.modalRodape}>
        <Button className={classes.botao} onClick={onClose}>Cancelar</Button>
        <Button className={classes.botao} color="primary" variant="contained" disableElevation onClick={salvar}
          disabled={salvando || (trocando && !token.trim() && !status?.token)} style={{ minWidth: 130 }}>
          {salvando ? <CircularProgress size={20} color="inherit" /> : "Salvar"}
        </Button>
      </div>
    </Dialog>
  );
};

const ChatIA = ({ open, onClose, periodo, perguntaInicial, onUsada }) => {
  const classes = useStyles();
  const [mensagens, setMensagens] = useState(() => {
    try { return JSON.parse(localStorage.getItem("metaChatIA") || "[]"); } catch (e) { return []; }
  });
  const [texto, setTexto] = useState("");
  const [pensando, setPensando] = useState(false);
  const fimRef = useRef(null);
  const entradaRef = useRef(null);
  useEffect(() => { try { localStorage.setItem("metaChatIA", JSON.stringify(mensagens.slice(-40))); } catch (e) { /* */ } }, [mensagens]);
  useEffect(() => { if (open) setTimeout(() => entradaRef.current?.focus(), 300); }, [open]);
  useEffect(() => { fimRef.current?.scrollIntoView({ behavior: "smooth" }); }, [mensagens, pensando]);

  const perguntar = async pergunta => {
    const p = String(pergunta || "").trim();
    if (!p || pensando) return;
    const novas = [...mensagens, { role: "user", content: p }];
    setMensagens(novas); setTexto(""); setPensando(true);
    try {
      const { data } = await api.post("/meta/ia", { mensagens: novas, periodo: paramsDe(periodo) });
      setMensagens(m => [...m, { role: "assistant", content: data.resposta }]);
    } catch (e) {
      setMensagens(m => [...m, { role: "assistant", content: `⚠️ ${e?.response?.data?.error || "Não consegui responder agora."}` }]);
    }
    setPensando(false);
  };
  const nomePeriodo = rotuloPeriodo(periodo);
  useEffect(() => {
    if (open && perguntaInicial) { onUsada(); perguntar(perguntaInicial); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, perguntaInicial]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <div className={classes.gaveta}>
        <div className={classes.gavetaTopo}>
          <AutoAwesomeIcon />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 17 }}>Analista de anúncios</div>
            <div style={{ fontSize: 12.5, opacity: .85 }}>Analisando: {nomePeriodo}</div>
          </div>
          {!!mensagens.length && <Tooltip title="Nova conversa"><IconButton style={{ color: "#fff" }} onClick={() => setMensagens([])}><DeleteSweepRoundedIcon /></IconButton></Tooltip>}
          <IconButton style={{ color: "#fff" }} onClick={onClose}><CloseRoundedIcon /></IconButton>
        </div>
        <div className={classes.msgs}>
          {!mensagens.length && (
            <>
              <div style={{ textAlign: "center", padding: "12px 8px 4px" }}>
                <div style={{ fontSize: 34 }}>👋</div>
                <div style={{ fontWeight: 700, fontSize: 16, marginTop: 6 }}>Oi! Sou sua especialista em Meta Ads</div>
                <div style={{ fontSize: 13, opacity: .65, marginTop: 4, lineHeight: 1.5 }}>
                  Pode me perguntar qualquer coisa sobre anúncios do Facebook e Instagram: como criar, segmentar, melhorar criativos,
                  baixar o custo... Eu também enxergo os números reais da sua conta. Escreva abaixo ou escolha uma sugestão:
                </div>
              </div>
              {ATALHOS.map(a => <button key={a} className={classes.sugestao} onClick={() => perguntar(a)}>{a}</button>)}
            </>
          )}
          {mensagens.map((m, i) => (
            <div key={i} className={`${classes.balao} ${m.role === "user" ? classes.eu : classes.ia}`}>
              {m.role === "user" ? m.content : <Formatado texto={m.content} />}
            </div>
          ))}
          {pensando && <div className={`${classes.balao} ${classes.ia}`} style={{ display: "flex", gap: 10, alignItems: "center" }}><CircularProgress size={16} /> Analisando seus números...</div>}
          <div ref={fimRef} />
        </div>
        <div className={classes.entrada}>
          <TextField fullWidth multiline maxRows={5} variant="outlined" placeholder="Escreva sua mensagem... (Enter envia)" inputRef={entradaRef}
            value={texto} onChange={e => setTexto(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); perguntar(texto); } }} />
          <IconButton className={classes.enviar} onClick={() => perguntar(texto)} disabled={pensando || !texto.trim()}><SendRoundedIcon /></IconButton>
        </div>
      </div>
    </Drawer>
  );
};

const Meta = () => {
  const classes = useStyles();
  const theme = useTheme();
  const pequeno = useMediaQuery(theme.breakpoints.down("xs"));
  const [status, setStatus] = useState(null);
  const [periodo, setPeriodo] = useState({ preset: "last_7d" });
  const [aberta, setAberta] = useState(null);
  const [perguntaIA, setPerguntaIA] = useState("");
  const [resumo, setResumo] = useState(null);
  const [campanhas, setCampanhas] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [config, setConfig] = useState(false);
  const [chat, setChat] = useState(false);
  const [busca, setBusca] = useState("");
  const [fStatus, setFStatus] = useState("todas");
  const [fGasto, setFGasto] = useState("todas");
  const [fObjetivo, setFObjetivo] = useState("todos");
  const [gastoMin, setGastoMin] = useState("");
  const [ordem, setOrdem] = useState({ campo: "gasto", desc: true });

  const carregarStatus = useCallback(async () => {
    try { const { data } = await api.get("/meta/status"); setStatus(data); return data; }
    catch (e) { toastError(e); return null; }
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true); setErro("");
    try {
      const [r, c] = await Promise.all([
        api.get("/meta/resumo", { params: paramsDe(periodo) }),
        api.get("/meta/campanhas", { params: paramsDe(periodo) }),
      ]);
      setResumo(r.data); setCampanhas(c.data || []);
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || "Erro";
      setErro(msg === "META_TOKEN_EXPIRADO" ? "O token da Meta expirou ou foi revogado. Clique em Configurar e cole um novo." : msg);
    }
    setCarregando(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveDe(periodo)]);

  useEffect(() => { carregarStatus(); }, [carregarStatus]);
  useEffect(() => { if (status?.configurado && status?.contaId && status?.valido !== false) carregar(); }, [status, carregar]);

  const renovar = async () => {
    try { await api.post("/meta/renovar-token"); toast.success("Token renovado por mais ~60 dias."); carregarStatus(); }
    catch (e) { toastError(e); }
  };

  const t = resumo?.totais || {};
  const expira = status?.expiraEm;
  const diasParaExpirar = expira && expira !== "nunca" ? Math.ceil((new Date(expira) - new Date()) / 86400000) : null;
  const corSelo = expira === "nunca" ? "#16a34a" : diasParaExpirar !== null && diasParaExpirar <= 7 ? "#dc2626" : "#d97706";

  const serie = useMemo(() => {
    const porDia = resumo?.porDia || [];
    const mapa = new Map(porDia.map(d => [d.dia, d]));
    const dias = diasDoPeriodo(periodo) || porDia.map(d => d.dia);
    return dias.map(d => ({ dia: `${d.slice(8, 10)}/${d.slice(5, 7)}`, gasto: mapa.get(d)?.gasto || 0, resultados: mapa.get(d)?.resultados || 0 }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumo, chaveDe(periodo)]);

  const comGasto = campanhas.filter(c => c.gasto > 0);
  const pizza = useMemo(() => {
    const top = comGasto.slice(0, 5).map(c => ({ nome: c.nome, valor: c.gasto }));
    const resto = comGasto.slice(5).reduce((s, c) => s + c.gasto, 0);
    if (resto > 0) top.push({ nome: "Outras", valor: resto });
    return top;
  }, [comGasto]);
  const custoPorCampanha = comGasto.filter(c => c.resultados > 0)
    .map(c => ({ nome: c.nome.length > 22 ? c.nome.slice(0, 22) + "…" : c.nome, custo: Number(c.custoPorResultado.toFixed(2)) }))
    .sort((a, b) => a.custo - b.custo).slice(0, 6);

  const grupoStatus = st => (st === "ACTIVE" ? "ativas" : /PAUSED/.test(st || "") ? "pausadas" : "outras");
  const objetivos = [...new Set(campanhas.map(objetivoDe).filter(Boolean))];
  const contagem = { todas: campanhas.length, ativas: 0, pausadas: 0, outras: 0 };
  campanhas.forEach(c => { contagem[grupoStatus(c.status)] += 1; });
  const min = Number(String(gastoMin).replace(",", ".")) || 0;
  const lista = campanhas
    .filter(c => fStatus === "todas" || grupoStatus(c.status) === fStatus)
    .filter(c => fGasto === "todas" || (fGasto === "com" ? c.gasto > 0 : c.gasto === 0))
    .filter(c => fObjetivo === "todos" || objetivoDe(c) === fObjetivo)
    .filter(c => c.gasto >= min)
    .filter(c => !busca.trim() || c.nome.toLowerCase().includes(busca.trim().toLowerCase()))
    .sort((x, y) => {
      const { campo, desc } = ordem;
      let a1 = x[campo], b1 = y[campo];
      if (campo === "custoPorResultado") { a1 = x.resultados ? a1 : Infinity; b1 = y.resultados ? b1 : Infinity; }
      if (campo === "nome") return desc ? String(b1).localeCompare(a1) : String(a1).localeCompare(b1);
      return desc ? (b1 > a1 ? 1 : b1 < a1 ? -1 : 0) : (a1 > b1 ? 1 : a1 < b1 ? -1 : 0);
    });
  const ordenar = campo => setOrdem(o => (o.campo === campo ? { campo, desc: !o.desc } : { campo, desc: campo !== "nome" && campo !== "custoPorResultado" }));
  const seta = campo => (ordem.campo === campo ? (ordem.desc ? " ↓" : " ↑") : "");
  const limpar = () => { setFStatus("todas"); setFGasto("todas"); setFObjetivo("todos"); setGastoMin(""); setBusca(""); };
  const menuSel = { MenuProps: { getContentAnchorEl: null, anchorOrigin: { vertical: "bottom", horizontal: "left" }, PaperProps: { style: { borderRadius: 12, marginTop: 6 } } } };
  const maior = Math.max(0, ...lista.map(c => c.gasto));

  const eixo = { fontSize: 12, fill: theme.palette.text.secondary };
  const configurada = status && status.configurado && status.contaId;

  return (
    <MainContainer>
      <div className={classes.pagina}>
        <div className={classes.topo}>
          {resumo?.conta?.nome && (
            <div className={classes.contaChip}>
              <span className={classes.contaPonto} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{resumo.conta.nome}</div>
                <div style={{ fontSize: 12, opacity: .6 }}>ID {resumo.conta.id}</div>
              </div>
            </div>
          )}
          <div className={classes.acoes}>
            {expira && (
              <span className={classes.selo} style={{ color: corSelo, background: fade(corSelo, .1) }}>
                ● {expira === "nunca" ? "Token não expira" : `Token expira em ${diasParaExpirar} dia(s)`}
              </span>
            )}
            {status?.appConfigurado && expira && expira !== "nunca" && (
              <Tooltip title="Renovar token por mais 60 dias"><IconButton className={classes.iconeBotao} onClick={renovar}><AutorenewRoundedIcon /></IconButton></Tooltip>
            )}
            {configurada && (
              <SeletorPeriodo valor={periodo} onChange={setPeriodo} />
            )}
            {configurada && <Tooltip title="Atualizar"><span><IconButton className={classes.iconeBotao} onClick={carregar} disabled={carregando}>
              {carregando ? <CircularProgress size={20} /> : <RefreshRoundedIcon />}</IconButton></span></Tooltip>}
            <Button variant="outlined" className={classes.botao} startIcon={<SettingsRoundedIcon />} onClick={() => setConfig(true)}>Configurar</Button>
          </div>
        </div>

        {status && !configurada ? (
          <div className={classes.aviso}>
            <div className={classes.logo} style={{ width: 72, height: 72, borderRadius: 22 }}><AllInclusiveRoundedIcon style={{ fontSize: 40 }} /></div>
            <div style={{ fontWeight: 800, fontSize: 20 }}>Conecte sua conta de anúncios</div>
            <div style={{ opacity: .7, maxWidth: 440 }}>Cole o token da Meta e escolha a conta para ver campanhas, gastos e conversar com a I.A analista.</div>
            <Button color="primary" variant="contained" disableElevation className={classes.botao} onClick={() => setConfig(true)}>Configurar agora</Button>
          </div>
        ) : (
          <>
            {(erro || status?.valido === false) && (
              <div className={classes.erro}>{erro || "O token da Meta expirou ou foi revogado. Clique em Configurar e cole um novo."}</div>
            )}
            {carregando && !resumo ? (
              <div style={{ textAlign: "center", padding: 60 }}><CircularProgress /></div>
            ) : resumo && (
              <>
                <div className={classes.herois}>
                  <Heroi icone={<PaymentsRoundedIcon />} cor={AZUL} rotulo="Valor gasto" valor={brl(t.gasto)} />
                  <Heroi icone={<FlagRoundedIcon />} cor={ROXO} rotulo="Resultados" valor={int(t.resultados)} />
                  <Heroi icone={<PriceCheckRoundedIcon />} cor="#14b8a6" rotulo="Custo por resultado" valor={t.resultados ? brl(t.custoPorResultado) : "—"} />
                  <Heroi icone={<ChatRoundedIcon />} cor="#f59e0b" rotulo="Conversas iniciadas" valor={int(t.conversas)} />
                </div>

                <div className={classes.faixa}>
                  {[
                    ["Impressões", int(t.impressoes)], ["Alcance", int(t.alcance)],
                    ["Frequência", Number(t.frequencia || 0).toFixed(2).replace(".", ",")], ["Cliques no link", int(t.cliquesNoLink)],
                    ["CTR", pct(t.ctr)], ["CPC", brl(t.cpc)], ["CPM", brl(t.cpm)], ["Leads", int(t.leads)],
                  ].map(([r, v]) => (
                    <div key={r} className={classes.mini}><div className={classes.miniRotulo}>{r}</div><div className={classes.miniValor}>{v}</div></div>
                  ))}
                </div>

                <div className={classes.graficos}>
                  <div className={classes.cartao}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
                      <div style={{ flex: 1 }}>
                        <div className={classes.cartaoTitulo}>Gasto e resultados por dia</div>
                        <div className={classes.cartaoSub}>Evolução no período selecionado</div>
                      </div>
                      <div className={classes.legenda}>
                        <span><i className={classes.ponto} style={{ background: AZUL }} />Gasto</span>
                        <span><i className={classes.ponto} style={{ background: ROXO }} />Resultados</span>
                      </div>
                    </div>
                    <ResponsiveContainer width="100%" height={280}>
                      <AreaChart data={serie} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="gGasto" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={AZUL} stopOpacity={0.35} /><stop offset="100%" stopColor={AZUL} stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="gRes" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={ROXO} stopOpacity={0.25} /><stop offset="100%" stopColor={ROXO} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid vertical={false} stroke={theme.palette.divider} />
                        <XAxis dataKey="dia" tick={eixo} axisLine={false} tickLine={false} minTickGap={16} />
                        <YAxis yAxisId="g" tick={eixo} axisLine={false} tickLine={false} width={58} tickFormatter={v => `R$ ${v}`} />
                        <YAxis yAxisId="r" orientation="right" tick={eixo} axisLine={false} tickLine={false} allowDecimals={false} width={32} />
                        <RTooltip content={<DicaGrafico />} />
                        <Area yAxisId="g" type="monotone" dataKey="gasto" name="Gasto" stroke={AZUL} strokeWidth={2.5} fill="url(#gGasto)" dot={false} activeDot={{ r: 5 }} />
                        <Area yAxisId="r" type="monotone" dataKey="resultados" name="Resultados" stroke={ROXO} strokeWidth={2.5} fill="url(#gRes)" dot={false} activeDot={{ r: 5 }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>

                  <div className={classes.cartao}>
                    <div className={classes.cartaoTitulo}>Onde a verba foi</div>
                    <div className={classes.cartaoSub}>Divisão do gasto por campanha</div>
                    {pizza.length ? (
                      <>
                        <div style={{ position: "relative" }}>
                          <ResponsiveContainer width="100%" height={190}>
                            <PieChart>
                              <Pie data={pizza} dataKey="valor" nameKey="nome" innerRadius={58} outerRadius={84} paddingAngle={2} stroke="none">
                                {pizza.map((_, i) => <Cell key={i} fill={CORES[i % CORES.length]} />)}
                              </Pie>
                              <RTooltip formatter={v => brl(v)} />
                            </PieChart>
                          </ResponsiveContainer>
                          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                            <div style={{ fontSize: 11, opacity: .6 }}>Total</div>
                            <div style={{ fontWeight: 800, fontSize: 16 }}>{brl(t.gasto)}</div>
                          </div>
                        </div>
                        <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
                          {pizza.map((p, i) => (
                            <div key={p.nome} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                              <i className={classes.ponto} style={{ background: CORES[i % CORES.length] }} />
                              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.nome}>{p.nome}</span>
                              <b>{t.gasto ? Math.round((p.valor / t.gasto) * 100) : 0}%</b>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : <div style={{ padding: 40, textAlign: "center", opacity: .6 }}>Sem gasto no período.</div>}
                  </div>
                </div>

                {custoPorCampanha.length > 1 && (
                  <div className={classes.cartao}>
                    <div className={classes.cartaoTitulo}>Custo por resultado</div>
                    <div className={classes.cartaoSub}>Da campanha mais barata para a mais cara</div>
                    <ResponsiveContainer width="100%" height={Math.max(160, custoPorCampanha.length * 44)}>
                      <BarChart data={custoPorCampanha} layout="vertical" margin={{ top: 12, right: 24, left: 8, bottom: 0 }}>
                        <CartesianGrid horizontal={false} stroke={theme.palette.divider} />
                        <XAxis type="number" tick={eixo} axisLine={false} tickLine={false} tickFormatter={v => `R$ ${v}`} />
                        <YAxis type="category" dataKey="nome" tick={eixo} axisLine={false} tickLine={false} width={pequeno ? 110 : 190} />
                        <RTooltip formatter={v => brl(v)} cursor={{ fill: fade(AZUL, .05) }} />
                        <Bar dataKey="custo" name="Custo por resultado" radius={[0, 8, 8, 0]} barSize={22}>
                          {custoPorCampanha.map((_, i) => <Cell key={i} fill={i === 0 ? "#14b8a6" : i === custoPorCampanha.length - 1 ? "#ef4444" : AZUL} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                <div className={classes.camps}>
                  <div className={classes.campsTopo}>
                    <div style={{ flex: 1, minWidth: 160 }}>
                      <div className={classes.cartaoTitulo}>Campanhas</div>
                      <div className={classes.cartaoSub}>{lista.length} de {campanhas.length} · clique numa campanha para ver os anúncios</div>
                    </div>
                    <TextField variant="outlined" placeholder="Buscar campanha" value={busca} onChange={e => setBusca(e.target.value)} className={classes.busca}
                      InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon style={{ opacity: .5 }} /></InputAdornment> }} />
                  </div>
                  <div className={classes.filtros}>
                    {[["todas", "Todas"], ["ativas", "Ativas"], ["pausadas", "Pausadas"], ["outras", "Outras"]].map(([v, r]) => (
                      <button key={v} className={`${classes.chipF} ${fStatus === v ? classes.chipAtivo : ""}`} onClick={() => setFStatus(v)}>
                        {r} <small>{contagem[v]}</small>
                      </button>
                    ))}
                    <div style={{ flex: 1 }} />
                    <TextField select variant="outlined" value={fGasto} onChange={e => setFGasto(e.target.value)} className={classes.filtroSel} SelectProps={menuSel}>
                      <MenuItem value="todas">Gasto: todas</MenuItem>
                      <MenuItem value="com">Com gasto</MenuItem>
                      <MenuItem value="sem">Sem gasto</MenuItem>
                    </TextField>
                    <TextField variant="outlined" value={gastoMin} onChange={e => setGastoMin(e.target.value.replace(/[^\d,.]/g, ""))} className={classes.filtroSel}
                      style={{ minWidth: 130, width: 130 }} placeholder="Gasto mín."
                      InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} inputProps={{ autoComplete: "off" }} />
                    {objetivos.length > 1 && (
                      <TextField select variant="outlined" value={fObjetivo} onChange={e => setFObjetivo(e.target.value)} className={classes.filtroSel} SelectProps={menuSel}>
                        <MenuItem value="todos">Objetivo: todos</MenuItem>
                        {objetivos.map(o => <MenuItem key={o} value={o}>{NOME_OBJ[o] || o}</MenuItem>)}
                      </TextField>
                    )}
                    <TextField select variant="outlined" value={`${ordem.campo}|${ordem.desc}`} className={classes.filtroSel} SelectProps={menuSel}
                      onChange={e => { const [campo, d] = e.target.value.split("|"); setOrdem({ campo, desc: d === "true" }); }}>
                      <MenuItem value="gasto|true">Maior gasto</MenuItem>
                      <MenuItem value="gasto|false">Menor gasto</MenuItem>
                      <MenuItem value="resultados|true">Mais resultados</MenuItem>
                      <MenuItem value="custoPorResultado|false">Menor custo/result.</MenuItem>
                      <MenuItem value="ctr|true">Maior CTR</MenuItem>
                      <MenuItem value="nome|false">Nome (A–Z)</MenuItem>
                    </TextField>
                    {(fStatus !== "todas" || fGasto !== "todas" || fObjetivo !== "todos" || gastoMin || busca) && (
                      <Button size="small" onClick={limpar} style={{ textTransform: "none", borderRadius: 10 }}>Limpar filtros</Button>
                    )}
                  </div>
                  <div className={classes.rolagem}>
                    <div className={classes.larg}>
                      <div className={`${classes.linha} ${classes.cab}`}>
                        <div className={classes.ordenavel} onClick={() => ordenar("nome")}>Campanha{seta("nome")}</div><div>Status</div>
                        {[["gasto", "Gasto"], ["impressoes", "Impressões"], ["cliques", "Cliques"], ["ctr", "CTR"], ["resultados", "Resultados"], ["custoPorResultado", "Custo/result."]].map(([k, r]) => (
                          <div key={k} className={`${classes.num} ${classes.ordenavel}`} onClick={() => ordenar(k)}>{r}{seta(k)}</div>
                        ))}
                        <div />
                      </div>
                      {lista.map(c => <LinhaCampanha key={c.id} c={c} maior={maior} onAbrir={setAberta} />)}
                      {!lista.length && <div style={{ padding: 32, textAlign: "center", opacity: .6, borderTop: `1px solid ${theme.palette.divider}` }}>Nenhuma campanha encontrada.</div>}
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {configurada && !chat && (
        <Fab variant="extended" className={classes.fab} onClick={() => setChat(true)}>
          <AutoAwesomeIcon /> Analista I.A
        </Fab>
      )}
      <CampanhaDrawer campanha={aberta} periodo={periodo} onClose={() => setAberta(null)}
        onPerguntar={q => { setAberta(null); setPerguntaIA(q); setChat(true); }} />
      <ChatIA open={chat} onClose={() => setChat(false)} periodo={periodo} perguntaInicial={perguntaIA} onUsada={() => setPerguntaIA("")} />
      <ConfigModal open={config} onClose={() => setConfig(false)} status={status} onSalvo={d => setStatus(d)} />
    </MainContainer>
  );
};

export default Meta;
